// # Email verification utility functions
import { auth, db } from '../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

// # Send verification email to user
export const sendVerificationEmail = async (user = null) => {
  try {
    // # Use current user if not provided
    const currentUser = user || auth.currentUser;
    
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }
    
    // # Send verification email
    await sendEmailVerification(currentUser);
    
    // # Return success
    return {
      success: true,
      message: 'Verification email sent successfully'
    };
  } catch (error) {
    console.error('Error sending verification email:', error);
    return {
      success: false,
      error: error.message || 'Failed to send verification email'
    };
  }
};

// # Check verification status from Firebase Auth
export const checkVerificationStatus = async (user = null) => {
  try {
    // # Use current user if not provided
    const currentUser = user || auth.currentUser;
    
    if (!currentUser) {
      throw new Error('No authenticated user found');
    }
    
    // # Force refresh to get latest status
    await currentUser.reload();
    
    // # Return verification status
    return {
      success: true,
      isVerified: currentUser.emailVerified
    };
  } catch (error) {
    console.error('Error checking verification status:', error);
    return {
      success: false,
      isVerified: false,
      error: error.message || 'Failed to check verification status'
    };
  }
};

// # Update user document with verification status
export const updateVerificationStatus = async (uid, isVerified) => {
  try {
    // # Update user document
    await updateDoc(doc(db, 'users', uid), {
      emailVerified: isVerified,
      'metadata.emailVerifiedAt': isVerified ? new Date().toISOString() : null,
      'metadata.updatedAt': new Date().toISOString()
    });
    
    // # Return success
    return {
      success: true,
      message: 'Verification status updated successfully'
    };
  } catch (error) {
    console.error('Error updating verification status:', error);
    return {
      success: false,
      error: error.message || 'Failed to update verification status'
    };
  }
};

// # Sync verification status between Auth and Firestore
export const syncVerificationStatus = async (uid = null) => {
  try {
    // # Use current user UID if not provided
    const currentUser = auth.currentUser;
    const userId = uid || (currentUser ? currentUser.uid : null);
    
    if (!userId) {
      throw new Error('No user ID provided');
    }
    
    // # Get the latest verification status from Auth
    await currentUser.reload();
    const isVerified = currentUser.emailVerified;
    
    // # Get current status from Firestore
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
    
    // # Update Firestore if status differs
    if (userDoc.data().emailVerified !== isVerified) {
      await updateVerificationStatus(userId, isVerified);
    }
    
    // # Return current status
    return {
      success: true,
      isVerified,
      message: 'Verification status synced successfully'
    };
  } catch (error) {
    console.error('Error syncing verification status:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync verification status'
    };
  }
};
